import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../app/store";
import { selectCompCodePath } from "../globalSelectors";
import {
  getValidationsForSpace,
  ISpaceValidation,
} from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import { selectActivePartialMcbimSpace } from "../McBim/mcbimSelectors";
import {
  selectActiveRoomHasStandardRoom,
  selectMlDevicesAvailableInSelectedSpace,
} from "../Room/roomSelectors";
import { selectActiveSingleRoomSummary } from "../Room/roomMcbimSharedSelectors";
import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { openapi, PostMcbimItemsApiResponse } from "../../openapi";
import { sortArrayByNumbers, sortArrayByStrings } from "../globalUtils";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { ROOM_SIDEBAR_TABS_BY_MODULE } from "@multiconsult-group/multiplacer-core";
import { selectHttpDevicesInRoom } from "./devices/deviceSlice";
import { selectHttpMcBimItemsInStandardSpace } from "../McBim/httpMcBimStandardSelectors";
import { selectHttpMcBimItemsInSelectedRoom } from "../RoomSideBar/singleRoomSelectors";

const selectProjectNo = (state: IRootState) => state.design.projectNo;

export const selectCachedMcbimBuildingStoriesForCurrentProject = createSelector(
  [selectProjectNo, (state: IRootState) => state],
  (projectNo, state) => {
    if (!projectNo) {
      return [];
    }

    return openapi.endpoints.getMcbimBuildingStories.select({ projectNo })(state).data ?? [];
  }
);

export const selectModRotation = (state: IRootState) => {
  const review = state.design.review;
  const rotation = state.design.rotation;
  return review.active === true ? 0 : rotation;
};

export const selectActiveRoomColoringId = (state: IRootState) => state.design.activeRoomColoringId;

const selectActiveSpaceValidationSpecs = (state: IRootState) =>
  state.design.activeSpaceValidationSpecs;

export const selectSpaceValidationForSelectedSpace = createSelector(
  [
    selectActiveSingleRoomSummary,
    selectActivePartialMcbimSpace,
    selectHttpDevicesInRoom,
    selectHttpMcBimItemsInStandardSpace,
    selectHttpMcBimItemsInSelectedRoom,
    selectActiveSpaceValidationSpecs,
    selectCompCodePath,
  ],
  (
    room,
    mcbimSpace,
    devicesInRoom,
    mcbimItemsInStandardSpace,
    mcbimItemsInSpace,
    activeSpaceValidationSpecs,
    compCodePath
  ) => {
    if (mcbimSpace == null) {
      return [];
    }
    // Här skulle vi kunna memoiza getValidationsForSpace för bättre prestanda
    return getValidationsForSpace({
      devicesInRoom,
      singleRoom: room as IRoomSummary,
      mcbimItemsInStandardSpace,
      mcbimItemsInSpace,
      singleSpace: mcbimSpace,
      activeSpaceValidationSpecs,
      compCodePath: compCodePath as string,
    });
  }
);

export const selectActiveSpaceHasNoWarnings = createSelector(
  [selectSpaceValidationForSelectedSpace],
  (validations): boolean => {
    return validations.every((validation: ISpaceValidation) => validation.status === "VALID");
  }
);

const selectActiveModuleSelector = (state: IRootState) => state.design.activeModule;
const selectProjectTab = (state: IRootState) => state.global.activeProjectTab;

const getAllRoomSideBarTabs = (
  roomHasMlPredictedDevices: boolean,
  roomHasStandardRoom: boolean
): RoomSideBarTabs[] => {
  let tabs: RoomSideBarTabs[] = Array.from(
    new Set(Object.values(ROOM_SIDEBAR_TABS_BY_MODULE).flat())
  );

  if (!roomHasMlPredictedDevices) {
    tabs = tabs.filter((tab) => tab !== RoomSideBarTabs.CEILING_AI_PREDICTIONS);
  }

  if (!roomHasStandardRoom) {
    tabs = tabs.filter((tab) => tab !== RoomSideBarTabs.CEILING_STANDARD_ROOM);
  }

  return tabs;
};

const ensureRoomDrofusChangesTab = (tabs: RoomSideBarTabs[]): RoomSideBarTabs[] =>
  tabs.includes(RoomSideBarTabs.ROOM_DROFUS_CHANGES)
    ? tabs
    : [...tabs, RoomSideBarTabs.ROOM_DROFUS_CHANGES];

export const selectAllRoomSideBarTabs = createSelector(
  [selectMlDevicesAvailableInSelectedSpace, selectActiveRoomHasStandardRoom],
  getAllRoomSideBarTabs
);

export const selectRoomSideBarTabs = createSelector(
  [
    selectActiveModuleSelector,
    selectMlDevicesAvailableInSelectedSpace,
    selectActiveRoomHasStandardRoom,
    selectProjectTab,
  ],
  (activeModule, roomHasMlPredictedDevices, roomHasStandardRoom, projectTab): RoomSideBarTabs[] => {
    // Specialfall: projectTab === "room" → visa alla tabs oavsett modul
    if (projectTab === "room") {
      return getAllRoomSideBarTabs(roomHasMlPredictedDevices, roomHasStandardRoom);
    }

    // Vanligt läge: styrs av aktiv modul
    if (!activeModule) {
      // No active module – show nothing
      return [];
    }

    let tabsForModule = ROOM_SIDEBAR_TABS_BY_MODULE[activeModule] ?? [];

    if (activeModule === "CEILING") {
      // Hide ML tab if no ML predictions for the room
      if (!roomHasMlPredictedDevices) {
        tabsForModule = tabsForModule.filter(
          (tab) => tab !== RoomSideBarTabs.CEILING_AI_PREDICTIONS
        );
      }

      // Hide standard room tab if the room has no standard room
      if (!roomHasStandardRoom) {
        tabsForModule = tabsForModule.filter(
          (tab) => tab !== RoomSideBarTabs.CEILING_STANDARD_ROOM
        );
      }
    }

    return ensureRoomDrofusChangesTab(tabsForModule);
  }
);

export const selectHttpMcbimBuildingStories = createSelector(
  [selectCachedMcbimBuildingStoriesForCurrentProject],
  (buildingStories) => {
    return (
      buildingStories?.map((d, index) => {
          return {
            index: index + 1,
            ...d,
          };
        }) ?? []
    );
  }
);

const selectBuildingStoryListState = (state: IRootState) => state.design.buildingStoryList;

export const selectBuildingStoryList = createSelector(
  [selectHttpMcbimBuildingStories, selectBuildingStoryListState],
  (buildingStories, buildingStoryListState) => {
    const { search, sortMode } = buildingStoryListState;

    const source = buildingStories;

    // 1) filtrering
    const q = search.trim().toLowerCase();
    const filtered = q ? source.filter((story) => story.name.toLowerCase().includes(q)) : source;

    // 2) sortering (alltid på en kopia)
    const sorted = filtered.slice();

    if (sortMode === "name") {
      return sortArrayByStrings(sorted, ["name"]);
    } else {
      return sortArrayByNumbers(sorted, ["spaceCount"], "desc");
    }
  }
);

// Norsk kommentar: Hent ut tydelige typer for selector-output
type TStories = ReturnType<typeof selectBuildingStoryList>;
type TStory = TStories extends readonly (infer U)[] ? U : never;

export interface IBuildingStoriesGroup<T> {
  building: string;
  floors: T[];
}

export const selectBuildingStoriesGroupedByBuilding = createSelector(
  [selectBuildingStoryList],
  (stories: TStories): IBuildingStoriesGroup<TStory>[] => {
    const byBuilding = new Map<string, TStory[]>();

    for (const story of stories) {
      // Norsk kommentar: building finnes ikke nødvendigvis i typen, så vi leser den defensivt
      const buildingRaw = (story as any).building as string | null | undefined;
      const building = (buildingRaw ?? "").trim() || "Unknown";

      const list = byBuilding.get(building);
      if (list) list.push(story);
      else byBuilding.set(building, [story]);
    }

    const groups: IBuildingStoriesGroup<TStory>[] = Array.from(byBuilding.entries()).map(
      ([building, floors]) => ({ building, floors })
    );

    // Norsk kommentar: Sorter bygg alfabetisk, men "Unknown" sist
    groups.sort((a, b) => {
      const aUnknown = a.building === "Unknown";
      const bUnknown = b.building === "Unknown";
      if (aUnknown && !bUnknown) return 1;
      if (!aUnknown && bUnknown) return -1;
      return a.building.localeCompare(b.building, "nb");
    });

    return groups;
  }
);

const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;
export const selectFloorElevation = (state: IRootState) => state.design.floorAndJob?.elevation ?? null;

// Returns the elevation (mm) of the floor directly above the current one, or null if top floor.
export const selectNextFloorElevation = createSelector(
  [selectHttpMcbimBuildingStories, selectFloorAndJob],
  (stories, floorAndJob) => {
    const current = floorAndJob?.elevation;
    if (current == null) return null;
    const above = stories
      .map((s) => (s as any).elevation as number | undefined)
      .filter((e): e is number => e != null && e > current)
      .sort((a, b) => a - b);
    return above[0] ?? null;
  }
);
export const selectProjectnoAndBuildingStoryArgs = createSelector(
  [selectProjectNo, selectFloorAndJob],
  (projectNo, currentBuildingStory) => {
    if (!projectNo || !currentBuildingStory) {
      return null;
    }
    return {
      projectNo,
      buildingStoryName: currentBuildingStory.floorName,
    };
  }
);

// Norsk (Bokmål): Returnerer valgte nøkler fra en ToggleMap.
const pickActiveKeys = (map: Partial<Record<string, boolean>>): string[] =>
  Object.entries(map)
    .filter(([, on]) => on === true)
    .map(([key]) => key);

export const selectMcBimItemsFilterMode = (state: IRootState) => state.design.mcbimItemsFilterMode;

export const selectSelectedIfcClasses = (state: IRootState) =>
  pickActiveKeys(state.design.ifcClassesToDisplay);

export const selectSelectedDisciplines = (state: IRootState) =>
  pickActiveKeys(state.design.disciplinesToDisplay);

const EMPTY_DISCIPLINE_LIST: string[] = [];

export const selectHttpUniqDisciplinesOnBuildingStory = createSelector(
  [selectProjectnoAndBuildingStoryArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) {
      return EMPTY_DISCIPLINE_LIST;
    }
    return openapi.endpoints.getMcbimDisciplines.select(args)(state).data ?? EMPTY_DISCIPLINE_LIST;
  }
);
